/** Concrete API client wrapper around the generic client.
 *
 *  Encapsulates:
 *    - admin base URL
 *    - token getter (delegates to authService)
 *    - 401 handling (clears stored token)
 */
import { createApiClient } from './api';
import { authService } from './auth';
import type { ApiRequestOptions } from '@/types';

const DEFAULT_BASE_URL =
  typeof window !== 'undefined' && window.location
    ? `${window.location.protocol}//${window.location.host}`
    : 'http://127.0.0.1:8000';

function createClient() {
  return createApiClient({
    baseUrl: DEFAULT_BASE_URL,
    getToken: () => authService.loadToken(),
    onUnauthorized: () => {
      // The next router guard will redirect to /login. We don't
      // navigate here to avoid side effects in services.
      authService.clear();
    },
  });
}

const client = createClient();

export interface ApiClient {
  get<T>(path: string, opts?: ApiRequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, opts?: ApiRequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, opts?: ApiRequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, opts?: ApiRequestOptions): Promise<T>;
}

export const apiClient: ApiClient = {
  async get<T>(path: string, opts: ApiRequestOptions = {}) {
    return client.request<T>(path, { ...opts, method: 'GET' });
  },
  async post<T>(path: string, body?: unknown, opts: ApiRequestOptions = {}) {
    return client.request<T>(path, { ...opts, method: 'POST', body });
  },
  async patch<T>(path: string, body?: unknown, opts: ApiRequestOptions = {}) {
    return client.request<T>(path, { ...opts, method: 'PATCH', body });
  },
  async put<T>(path: string, body?: unknown, opts: ApiRequestOptions = {}) {
    return client.request<T>(path, { ...opts, method: 'PUT', body });
  },
};

export { DEFAULT_BASE_URL };